import React from 'react';

/**
 * El maletín, dibujado en SVG: cuero marrón con herrajes de bronce.
 *
 * La versión anterior era metálica gris y quedaba fría, más parecida a un flight case que al
 * maletín de cuero del programa. Acá el cuerpo es cuero con costura perimetral, refuerzos en las
 * esquinas, cerrojos y manija de bronce.
 *
 * No se usa textura por filtro (feTurbulence) a propósito: con diez maletines en pantalla, diez
 * filtros de ruido cuestan caro en móvil. El grano se sugiere con capas de degradé.
 */
export default function Maletin({ numero, estado = 'cerrado', pista = null }) {
    const mio = estado === 'mio';

    // El tuyo es DORADO, no cuero: tiene que cantar entre los otros nueve. El coñac oscuro que
    // tenía antes era un marrón más y se perdía en la grilla.
    const cuero = mio
        ? { alto: '#ffe9a3', medio: '#e8b430', bajo: '#a9740c', borde: '#4a3105' }
        : { alto: '#b9834b', medio: '#96603a', bajo: '#5e3a22', borde: '#3d2416' };
    const bronce = mio
        ? { claro: '#fffbe8', medio: '#ffd977', oscuro: '#8a6108' }
        : { claro: '#d9b168', medio: '#a8813c', oscuro: '#6b4d16' };
    const id = `${estado}-${numero}`;

    return (
        <svg viewBox="0 0 120 108" className="mal-svg" aria-hidden="true">
            <defs>
                <linearGradient id={`cuero-${id}`} x1="0" y1="0" x2="0.15" y2="1">
                    <stop offset="0%" stopColor={cuero.alto} />
                    <stop offset="34%" stopColor={cuero.medio} />
                    <stop offset="49%" stopColor={cuero.bajo} />
                    <stop offset="53%" stopColor={cuero.medio} />
                    <stop offset="100%" stopColor={cuero.bajo} />
                </linearGradient>
                {/* Brillo suave y ancho: el cuero refleja difuso, no como el metal */}
                <linearGradient id={`lustre-${id}`} x1="0.1" y1="0" x2="0.9" y2="1">
                    <stop offset="0%" stopColor="#fff" stopOpacity="0.16" />
                    <stop offset="45%" stopColor="#fff" stopOpacity="0.03" />
                    <stop offset="100%" stopColor="#000" stopOpacity="0.18" />
                </linearGradient>
                <linearGradient id={`bronce-${id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={bronce.claro} />
                    <stop offset="55%" stopColor={bronce.medio} />
                    <stop offset="100%" stopColor={bronce.oscuro} />
                </linearGradient>
                <linearGradient id={`haz-${id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffe9a3" stopOpacity="0.42" />
                    <stop offset="100%" stopColor="#ffe9a3" stopOpacity="0" />
                </linearGradient>
                <radialGradient id={`foco-${id}`} cx="0.5" cy="0.5">
                    <stop offset="0%" stopColor={mio ? '#ffd977' : '#38bdf8'} stopOpacity={mio ? 0.8 : 0.38} />
                    <stop offset="100%" stopColor={mio ? '#ffd977' : '#38bdf8'} stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* Halo del foco sobre el podio */}
            <ellipse cx="60" cy="97" rx={mio ? 54 : 46} ry={mio ? 11 : 8} fill={`url(#foco-${id})`} />

            {/* Haz de luz cenital, solo sobre el tuyo */}
            {mio && <path d="M60 -16 L98 94 L22 94 Z" fill={`url(#haz-${id})`} />}

            {/* Manija de cuero con la base de bronce */}
            <path d="M47 21 v-5 a13 8.5 0 0 1 26 0 v5" fill="none" stroke={cuero.borde} strokeWidth="8" strokeLinecap="round" />
            <path d="M47 21 v-5 a13 8.5 0 0 1 26 0 v5" fill="none" stroke={cuero.medio} strokeWidth="4.5" strokeLinecap="round" />
            <rect x="43" y="19" width="8" height="6" rx="1.5" fill={`url(#bronce-${id})`} />
            <rect x="69" y="19" width="8" height="6" rx="1.5" fill={`url(#bronce-${id})`} />

            {/* Cuerpo */}
            <rect x="12" y="22" width="96" height="64" rx="8" fill={`url(#cuero-${id})`} stroke={cuero.borde} strokeWidth="2.5" />
            <rect x="12" y="22" width="96" height="64" rx="8" fill={`url(#lustre-${id})`} />

            {/* Costura perimetral: es lo que más lo lee como cuero */}
            <rect x="17.5" y="27.5" width="85" height="53" rx="5"
                fill="none" stroke="#f0dcb4" strokeWidth="1" strokeDasharray="3 3.5" opacity="0.5" />

            {/* Junta de las dos tapas */}
            <rect x="12" y="52.5" width="96" height="3" fill={cuero.borde} opacity="0.85" />
            <rect x="12" y="55.5" width="96" height="1" fill="#fff" opacity="0.06" />

            {/* Cerrojos de bronce */}
            {[38, 82].map(x => (
                <g key={x}>
                    <rect x={x - 8} y="46" width="16" height="15" rx="2.5" fill={`url(#bronce-${id})`} stroke={cuero.borde} strokeWidth="1.2" />
                    <rect x={x - 4.5} y="50" width="9" height="3.5" rx="1" fill={cuero.bajo} opacity="0.8" />
                    <circle cx={x} cy="57.5" r="1.4" fill={bronce.claro} opacity="0.85" />
                </g>
            ))}

            {/* Refuerzos de esquina */}
            {[[12, 22, 0], [108, 22, 90], [12, 86, 270], [108, 86, 180]].map(([x, y, rot], i) => (
                <path key={i} d="M0 9 A9 9 0 0 1 9 0 L9 3.4 A5.6 5.6 0 0 0 3.4 9 Z"
                    transform={`translate(${x} ${y}) rotate(${rot})`}
                    fill={`url(#bronce-${id})`} opacity="0.92" />
            ))}

            {/* Placa con el número */}
            <rect x="46" y="31" width="28" height="15" rx="2.5" fill={`url(#bronce-${id})`} stroke={cuero.borde} strokeWidth="1" />
            <text x="60" y="41.8" textAnchor="middle" className="mal-numero" fill={cuero.borde}>{numero}</text>

            {/* Cinta que lo identifica como tuyo */}
            {mio && (
                <g>
                    <path d="M6 29 h34 l-7 7.5 7 7.5 H6 Z" fill="#e11d2e" stroke="#7f1010" strokeWidth="1.2" />
                    <text x="21" y="39.4" textAnchor="middle" className="mal-cinta" fill="#fff">TUYO</text>
                </g>
            )}

            {/* Pista del beneficio, en una etiqueta colgada de la tapa inferior */}
            {pista && (
                <>
                    <rect x="19" y="63" width="82" height="15" rx="2.5" fill="#f0dcb4" opacity="0.93" />
                    <text x="60" y="73.8" textAnchor="middle" className="mal-pista" fill="#3d2416">{pista}</text>
                </>
            )}
        </svg>
    );
}
